import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchJson } from '../api';
import PollCard from '../components/PollCard';
import { BoothIcon } from '../components/Icons';
import { useAuth } from '../lib/auth';
import { formatCount } from '../lib/format';

const PAGE_SIZE = 12;

function safeStorageGet(key) {
  try { return window.localStorage.getItem(key); } catch (_) { return null; }
}
function safeStorageSet(key, value) {
  try { window.localStorage.setItem(key, value); } catch (_) {}
}

function WelcomeBackBanner() {
  const auth = useAuth();
  const user = auth?.user;
  const [dismissed, setDismissed] = useState(() => safeStorageGet('voto-welcome-back-dismissed') === '1');
  const archive = user?.archive;
  if (!archive || dismissed) return null;
  const votes = Number(archive.votes_total || 0).toLocaleString();
  const pollCount = Number(archive.polls_count || 0).toLocaleString();
  return (
    <div className="mx-3.5 my-3 rounded-xl bg-voto-surface p-4 ring-1 ring-voto-line">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-base font-bold text-voto-fg">Welcome back, {archive.first_name || archive.handle}.</p>
          <p className="mt-1 text-sm text-voto-muted">
            Your {pollCount} polls and {votes} votes are home.{' '}
            <Link to={`/u/${archive.handle}`} className="font-semibold text-voto-coral hover:underline">
              Visit your archive →
            </Link>
          </p>
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          className="rounded px-1 text-lg leading-none text-voto-muted hover:text-voto-fg"
          onClick={() => { safeStorageSet('voto-welcome-back-dismissed', '1'); setDismissed(true); }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

function formatFeaturedDate(dateString) {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function utcYear(dateString) {
  if (!dateString) return '';
  const y = new Date(dateString).getUTCFullYear();
  return Number.isNaN(y) ? '' : String(y);
}

// The pill row (frame B). Only "For you" is live this round; the Trending and
// tag pills are a later slot (spec §1, §7) and are deliberately not rendered.
function PillRow() {
  return (
    <nav aria-label="Feed" className="bg-voto-chrome px-4 pb-2.5">
      <ul className="flex gap-2 overflow-x-auto scrollbar-none">
        <li>
          <Link
            to="/feed"
            aria-current="page"
            className="block border-b-2 border-voto-coral px-1 pb-1.5 pt-1 text-[13px] font-medium text-white"
          >
            For you
          </Link>
        </li>
      </ul>
    </nav>
  );
}

function BoothStrip({ waiting }) {
  const known = Number.isFinite(waiting);
  return (
    <Link
      to="/booth"
      data-testid="booth-strip"
      className="flex items-center gap-3 bg-voto-surface px-3.5 py-3 transition hover:bg-voto-raised"
    >
      <BoothIcon className="h-5 w-5 flex-none text-voto-coral" />
      <span className="min-w-0 flex-1 truncate text-sm">
        <span className="font-semibold text-voto-fg">Voting Booth</span>
        <span className="text-voto-muted">
          {known ? ` · ${formatCount(waiting)} waiting` : ''} · earn credits
        </span>
      </span>
      <span aria-hidden="true" className="text-lg leading-none text-voto-muted">›</span>
    </Link>
  );
}

export default function FeedPage() {
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const activeTag = searchParams.get('tag') || '';
  const [polls, setPolls] = useState([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState('');
  const [potd, setPotd] = useState(null);
  const [waiting, setWaiting] = useState(null);

  // pick up an archive claim linked since the session started
  useEffect(() => {
    auth?.refreshUser?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pageUrl(nextOffset) {
    // A tag link (from /tags or a poll's tags) keeps the plain tag filter; the
    // feed itself is the one "For you" order.
    if (activeTag) return `/polls?limit=${PAGE_SIZE}&offset=${nextOffset}&tag=${encodeURIComponent(activeTag)}`;
    return `/polls?sort=foryou&limit=${PAGE_SIZE}&offset=${nextOffset}`;
  }

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setPolls([]);
    setOffset(0);
    setError('');
    setPotd(null);
    setWaiting(null);

    if (!activeTag) {
      fetchJson('/polls/potd', { signal: controller.signal })
        .then((data) => { if (data?.potd) setPotd(data.potd); })
        .catch(() => {});
      fetchJson('/booth/next?limit=1', { signal: controller.signal })
        .then((data) => { if (Number.isFinite(data?.waiting)) setWaiting(data.waiting); })
        .catch(() => {});
    }

    fetchJson(pageUrl(0), { signal: controller.signal })
      .then((data) => {
        const rows = Array.isArray(data) ? data : [];
        setPolls(rows);
        setOffset(rows.length);
        setHasMore(rows.length === PAGE_SIZE);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setError('Unable to load polls right now.');
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTag]);

  async function loadMore() {
    setLoadingMore(true);
    setError('');
    try {
      const data = await fetchJson(pageUrl(offset));
      const rows = Array.isArray(data) ? data : [];
      setPolls((current) => {
        const seen = new Set(current.map((p) => p.id));
        return [...current, ...rows.filter((p) => !seen.has(p.id))];
      });
      setOffset((current) => current + rows.length);
      setHasMore(rows.length === PAGE_SIZE);
    } catch (err) {
      setError('Unable to load more polls right now.');
    } finally {
      setLoadingMore(false);
    }
  }

  // Poll of the Day leads; never show it twice.
  const cards = potd ? polls.filter((p) => p.id !== potd.id) : polls;
  const potdLabel = potd
    ? potd.poll_of_the_day_at
      ? `first featured ${formatFeaturedDate(potd.poll_of_the_day_at)}`
      : `created ${formatFeaturedDate(potd.created_at)}`
    : '';
  const potdYear = potd ? utcYear(potd.poll_of_the_day_at || potd.created_at) : '';

  return (
    <main className="mx-auto w-full max-w-xl pb-6">
      <PillRow />
      <WelcomeBackBanner />

      {activeTag && (
        <div className="flex items-center gap-3 px-3.5 py-3">
          <span className="rounded-full bg-voto-surface px-3 py-1 text-sm font-medium text-voto-fg ring-1 ring-voto-line">
            #{activeTag}
          </span>
          <Link to="/feed" className="rounded text-sm font-medium text-voto-muted hover:text-voto-fg">
            Clear filter ×
          </Link>
        </div>
      )}

      <div className="flex flex-col gap-1.5" data-testid="feed">
        {potd && (
          <section aria-label="Poll of the Day" data-testid="potd">
            <PollCard
              poll={potd}
              to={`/poll/${potd.id}`}
              eyebrow={`Poll of the day${potdYear ? ` · ${potdYear}` : ''}`}
              byline={potdLabel}
            />
          </section>
        )}

        {!activeTag && <BoothStrip waiting={waiting} />}

        {loading && <p className="px-4 py-8 text-voto-muted">Loading polls…</p>}
        {!loading && error && <p className="mx-3.5 mt-4 rounded-lg bg-voto-coral/10 p-3 text-voto-coral">{error}</p>}
        {!loading && !error && cards.length === 0 && (
          <p className="mx-3.5 mt-4 rounded-lg bg-voto-surface p-4 text-voto-muted">No polls found yet.</p>
        )}

        {cards.map((poll) => (
          <PollCard key={poll.id} poll={poll} to={`/poll/${poll.id}`} />
        ))}
      </div>

      {!loading && hasMore && cards.length > 0 && (
        <div className="mt-6 flex justify-center">
          <button
            className="rounded-lg bg-voto-surface px-5 py-2 text-sm font-medium text-voto-fg ring-1 ring-voto-line transition hover:bg-voto-raised disabled:cursor-not-allowed disabled:opacity-60"
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </main>
  );
}
